import { NODES_TRANSFORMER_ID } from './constants';
import expandSelectionIdsForTransformer from './expandSelectionIdsForTransformer';

/** Gap between selection dotted border bottom and toolbar top (px). */
export const FLOATING_TOOLBAR_GAP_BELOW_SELECTION_PX = 0;

const CANVAS_CONTAINER_SELECTOR = '.respark_canvas-container';

const unionClientRects = (rects) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  rects.forEach(({ x, y, width, height }) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  });

  return { minX, minY, maxX, maxY };
};

const transformerMatchesSelection = (transformer, expectedIds) => {
  if (!transformer || expectedIds.length === 0) {
    return false;
  }

  const attached = transformer.nodes?.() ?? [];
  if (attached.length !== expectedIds.length) {
    return false;
  }

  const attachedIds = new Set(attached.map((node) => node.id()));
  return expectedIds.every((id) => attachedIds.has(id));
};

/**
 * Map a Konva rect (stage space) to CSS pixels inside the canvas container.
 */
const mapStageRectToContainerAnchor = (stage, stageBox) => {
  const container = stage.container()?.closest(CANVAS_CONTAINER_SELECTOR);
  const stageEl = stage.container();

  if (!container || !stageEl) {
    return {
      left: stageBox.x + stageBox.width / 2,
      top: stageBox.y + stageBox.height,
    };
  }

  const stageDom = stageEl.getBoundingClientRect();
  const containerDom = container.getBoundingClientRect();
  const stageWidth = stage.width();
  const stageHeight = stage.height();

  const scaleX = stageWidth > 0 ? stageDom.width / stageWidth : 1;
  const scaleY = stageHeight > 0 ? stageDom.height / stageHeight : 1;

  return {
    left:
      stageDom.left -
      containerDom.left +
      (stageBox.x + stageBox.width / 2) * scaleX,
    top:
      stageDom.top -
      containerDom.top +
      (stageBox.y + stageBox.height) * scaleY,
  };
};

/**
 * Anchor for the floating selection toolbar: horizontal center, 5px below the
 * selection transformer dotted box.
 *
 * @param {import('konva/lib/Layer').Layer | undefined} designLayer
 * @param {string[]} selectionIds
 * @param {Record<string, object>} [annotations]
 * @returns {{ left: number, top: number } | null}
 */
const measureSelectionToolbarPosition = (
  designLayer,
  selectionIds,
  annotations = {},
) => {
  if (!designLayer || !selectionIds?.length) {
    return null;
  }

  const stage = designLayer.getStage?.();
  if (!stage) {
    return null;
  }

  const gap = FLOATING_TOOLBAR_GAP_BELOW_SELECTION_PX;
  const transformerIds = expandSelectionIdsForTransformer(selectionIds, annotations);
  const transformer = stage.findOne(`#${NODES_TRANSFORMER_ID}`);

  let stageBox = null;

  if (transformer && transformerMatchesSelection(transformer, transformerIds)) {
    stageBox = transformer.getClientRect({ relativeTo: stage });
  } else {
    const nodeRects = transformerIds
      .map((id) => designLayer.findOne(`#${id}`))
      .filter(Boolean)
      .map((node) => node.getClientRect({ relativeTo: stage }));

    if (!nodeRects.length) {
      return null;
    }

    const { minX, minY, maxX, maxY } = unionClientRects(nodeRects);
    stageBox = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  const anchor = mapStageRectToContainerAnchor(stage, stageBox);

  return {
    left: anchor.left,
    top: anchor.top + gap,
  };
};

export default measureSelectionToolbarPosition;
